import React, { useState, useEffect, useRef } from 'react';
import { X, Search, User, Calendar, FileText } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import { formatForDateTimeInput } from '../../../utils/timezoneUtils';

const TaskModal = ({
  isOpen,
  onClose,
  onSubmit,
  isSubmitting = false,
  initialTask = null,
  projectId
}) => {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    start_datetime: '',
    end_datetime: '',
    assigned_to: ''
  });
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [error, setError] = useState(null);

  const titleInputRef = useRef(null);
  const searchInputRef = useRef(null);

  // Check if current user is admin
  const isAdmin = currentUser?.roles && Array.isArray(currentUser.roles) &&
    (currentUser.roles.includes(1) || currentUser.roles.some(role => role.id === 1));

  // Initialize form data when modal opens or task changes
  useEffect(() => {
    if (isOpen) {
      if (initialTask) {
        setFormData({
          title: initialTask.title || '',
          description: initialTask.description || '',
          start_datetime: formatForDateTimeInput(initialTask.start_datetime),
          end_datetime: formatForDateTimeInput(initialTask.end_datetime),
          assigned_to: initialTask.assigned_to || ''
        });
      } else {
        setFormData({
          title: '',
          description: '',
          start_datetime: '',
          end_datetime: '',
          assigned_to: ''
        });
      }
      setError(null);

      // Focus title input after a short delay
      setTimeout(() => {
        titleInputRef.current?.focus();
      }, 100);

      // Fetch users if admin
      if (isAdmin) {
        fetchUsers();
      }
    }
  }, [isOpen, initialTask, isAdmin]);

  // Filter users based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(user =>
        (user.full_name || user.username).toLowerCase().includes(searchTerm.toLowerCase()) ||
        user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (user.email && user.email.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredUsers(filtered);
    }
  }, [users, searchTerm]);

  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const token = localStorage.getItem('token');
      const response = await fetch('/api/project-tracker/users', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data);
        setFilteredUsers(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim()) {
      setError('Task title is required');
      return;
    }

    setError(null);
    try {
      const submitData = {
        ...formData,
        project_id: projectId
      };
      await onSubmit(submitData);
      onClose();
    } catch (error) {
      console.error('Failed to save task:', error);
      setError(error.message || 'Failed to save task');
    }
  };

  const handleClose = () => {
    setFormData({
      title: '',
      description: '',
      start_datetime: '',
      end_datetime: '',
      assigned_to: ''
    });
    setSearchTerm('');
    setError(null);
    onClose();
  };

  const selectedUser = users.find(u => u.id.toString() === formData.assigned_to.toString());

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-lg border border-slate-700 w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-700">
          <h2 className="text-lg font-bold text-white">
            {initialTask ? 'Edit Task' : 'Add Task'}
          </h2>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 text-slate-400 hover:text-white rounded-lg hover:bg-slate-700 transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          <form onSubmit={handleSubmit} className="space-y-4">

            {/* Error Message */}
            {error && (
              <div className="p-3 bg-red-900/50 border border-red-700/50 rounded-lg">
                <p className="text-red-200 text-sm">{error}</p>
              </div>
            )}

            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Title <span className="text-red-400">*</span>
              </label>
              <input
                ref={titleInputRef}
                type="text"
                value={formData.title}
                onChange={(e) => {
                  setFormData({ ...formData, title: e.target.value });
                  if (error) setError(null);
                }}
                placeholder="Enter task title..."
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                required
              />
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-1">
                Description
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Enter task description..."
                className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 resize-none transition-colors"
                rows="2"
              />
            </div>

            {/* Schedule */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Start Date */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Start Date
                </label>
                <input
                  type="datetime-local"
                  value={formData.start_datetime}
                  onChange={(e) => setFormData({ ...formData, start_datetime: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                />
              </div>

              {/* End Date */}
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Due Date
                </label>
                <input
                  type="datetime-local"
                  value={formData.end_datetime}
                  onChange={(e) => setFormData({ ...formData, end_datetime: e.target.value })}
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                />
              </div>
            </div>

            {/* Assignment - Admin Only */}
            {isAdmin && (
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-1">
                  Assign to User
                </label>

                {/* Search Input */}
                <div className="relative mb-2">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 bg-slate-700 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm"
                  />
                </div>

                {/* User List */}
                <div className="max-h-32 overflow-y-auto border border-slate-600 rounded-lg bg-slate-700/20">
                  {loadingUsers ? (
                    <div className="flex items-center justify-center py-4">
                      <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                    </div>
                  ) : (
                    <>
                      {/* Unassigned Option */}
                      <label className="flex items-center space-x-2 p-2 hover:bg-slate-700/50 cursor-pointer border-b border-slate-600/30">
                        <input
                          type="radio"
                          name="assignee"
                          value=""
                          checked={formData.assigned_to === ''}
                          onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                          className="w-3 h-3 text-blue-600 bg-slate-700 border-slate-600 focus:ring-blue-500"
                        />
                        <div className="w-6 h-6 bg-slate-600 rounded-full flex items-center justify-center">
                          <User className="w-3 h-3 text-slate-400" />
                        </div>
                        <div className="flex-1">
                          <div className="text-sm font-medium text-slate-300">Unassigned</div>
                        </div>
                      </label>

                      {/* User Options */}
                      {filteredUsers.map((user) => (
                        <label
                          key={user.id}
                          className="flex items-center space-x-2 p-2 hover:bg-slate-700/50 cursor-pointer border-b border-slate-600/30 last:border-b-0"
                        >
                          <input
                            type="radio"
                            name="assignee"
                            value={user.id}
                            checked={formData.assigned_to.toString() === user.id.toString()}
                            onChange={(e) => setFormData({ ...formData, assigned_to: e.target.value })}
                            className="w-3 h-3 text-blue-600 bg-slate-700 border-slate-600 focus:ring-blue-500"
                          />
                          <div className="w-6 h-6 bg-slate-600 rounded-full flex items-center justify-center">
                            <span className="text-xs font-medium text-white">
                              {(user.full_name || user.username).charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-medium text-slate-200 truncate">
                              {user.full_name || user.username}
                            </div>
                            {user.full_name && user.username !== user.full_name && (
                              <div className="text-xs text-slate-500 truncate">
                                @{user.username}
                              </div>
                            )}
                          </div>
                        </label>
                      ))}

                      {filteredUsers.length === 0 && searchTerm && !loadingUsers && (
                        <div className="text-center py-4 text-slate-500">
                          <User className="w-6 h-6 mx-auto mb-1 text-slate-600" />
                          <p className="text-sm">No users found matching "{searchTerm}"</p>
                        </div>
                      )}
                      </>
                    )}
                  </div>
                </div>
            )}
          </form>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end space-x-3 p-4 border-t border-slate-700">
          <button
            type="button"
            onClick={handleClose}
            disabled={isSubmitting}
            className="px-4 py-2 text-slate-400 hover:text-white border border-slate-600 hover:border-slate-500 rounded-lg transition-colors text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={isSubmitting || !formData.title.trim()}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-slate-600 text-white rounded-lg transition-colors flex items-center space-x-2 text-sm"
          >
            {isSubmitting && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
            )}
            <span>{isSubmitting ? 'Saving...' : (initialTask ? 'Update Task' : 'Create Task')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default TaskModal;